/**
 * scheduleTemplates.js — ECSBC 2024 / ASHRAE 90.1 schedule templates
 *
 * 24-hour profiles:  index 0 = 00:00, index 23 = 23:00
 * Fraction values:   0.0 – 1.0  (displayed as 0 – 100%)
 * Setpoint values:   °C
 *
 * The operating profiles are those of the CODE VERSION the project selects on the Schedules
 * screen, overlaid onto the matching building types. One fully-overlaid template set is built per
 * code version (TEMPLATES_BY_CODE); read values through templatesFor(code), never BASE_TEMPLATES.
 *
 * How much each code overlays differs, so do not assume a uniform set:
 *   ECSBC 2024 §12.7 / ECBC 2017 §9.6 tabulate occupancy, interior + exterior lighting, interior
 *     equipment and HVAC availability only. Infiltration, outdoor air and thermostat setpoints are
 *     in neither code's schedule tables and keep the engineering defaults in BASE_TEMPLATES.
 *   ASHRAE 90.1 (both vintages) comes from the DOE prototype models, which DO carry infiltration,
 *     outdoor air and setpoints, so those are overlaid too. Where a prototype has no object for a
 *     role, that role is simply absent from the profile and BASE_TEMPLATES still supplies it -
 *     see fallbackRoles in ashraeScheduleProfiles.js.
 */

import { ECSBC_2024_SCHEDULE_PROFILES } from './ecsbc2024ScheduleProfiles.js';
import { ECBC_2017_SCHEDULE_PROFILES, ECBC_2017_SCHEDULE_SOURCES } from './ecbc2017ScheduleProfiles.js';
import { ASHRAE_SCHEDULE_PROFILES, ASHRAE_SCHEDULE_SOURCES } from './ashraeScheduleProfiles.js';

// ── Selectable schedule code versions ─────────────────────────────────────────
// The project picks ONE, on the Schedules screen. Whichever is picked drives the design case AND
// the compliance baseline identically — ECBC §9.4.1 Table 9-1 row 3, ECSBC Table 12-1 and ASHRAE
// 90.1 Appendix G Table G3.1 item 4 all require the Standard/Baseline Design to use the same
// operating schedules as the Proposed.
//
// `requiresProfile` marks a code that may only be picked for building types it actually tabulates.
// ECSBC and ECBC fall back to the app's engineering defaults for the types they omit and say so
// (scheduleSource returns a note). ASHRAE has no such fallback story: labelling the app's own
// default profiles "ASHRAE 90.1" would misattribute them, so it is blocked for those types instead.
export const SCHEDULE_CODES = [
  { id: 'ecsbc_2024', label: 'ECSBC 2024', section: '§12.7', ref: 'Tables 12.12 – 12.35',
    desc: 'Energy Conservation & Sustainable Building Code 2024 operating schedules.' },
  { id: 'ecbc_2017',  label: 'ECBC 2017',  section: '§9.6',  ref: 'Tables 9-12 – 9-36',
    desc: 'Energy Conservation Building Code 2017 operating schedules (Whole Building Performance Method).' },
  // ONE ASHRAE set. The DOE prototype library also carries _2010 and _2013 variants of interior
  // lighting and equipment (and no 2022 variant at all), but the app deliberately ships a single
  // option built from the 2004-vintage objects. Against the 2013 prototypes that is roughly 13%
  // more lighting hours for office and educational; under Appendix G it largely cancels, because
  // baseline and proposed share schedules (G3.1 item 4), so it moves absolute EUI, not the rating.
  { id: 'ashrae_901', label: 'ASHRAE 90.1', section: 'App G',
    ref: 'DOE prototype schedules',
    desc: 'ASHRAE 90.1 operating schedules from the DOE prototype building models. Appendix G Table G3.1 item 4 publishes no schedule table of its own, so these come from the prototype models behind the standard, not from a clause of it.',
    requiresProfile: true },
];
export const DEFAULT_SCHEDULE_CODE = 'ecsbc_2024';
// Null-prototype on purpose: a plain object literal would inherit Object.prototype, so a stored
// value of "constructor" or "toString" would look like a valid code id and be returned as one.
export const SCHEDULE_CODE_MAP = Object.assign(
  Object.create(null),
  Object.fromEntries(SCHEDULE_CODES.map((c) => [c.id, c])),
);

// A vintage split (two ASHRAE ids) was briefly shipped and then withdrawn. Map both of its ids
// back onto the single set rather than letting them fall to the default code, which would
// silently re-base every schedule in such a project from ASHRAE to ECSBC.
const LEGACY_SCHEDULE_CODE_IDS = Object.assign(Object.create(null), {
  ashrae_901_2004: 'ashrae_901',
  ashrae_901_2013: 'ashrae_901',
});

/** Normalise any stored/absent value to a code id that exists. */
export const resolveScheduleCode = (v) => {
  const id = LEGACY_SCHEDULE_CODE_IDS[v] || v;
  return SCHEDULE_CODE_MAP[id] ? id : DEFAULT_SCHEDULE_CODE;
};

// ── Schedule type definitions ──────────────────────────────────────────────────

// ECSBC 2024 §12.7 — lighting and equipment are split into interior and exterior
// to comply with the standard's separate LPD/EPD categories for each.
export const SCHEDULE_TYPES = [
  { id: 'occupancy',          label: 'Occupancy',              unit: '%',      color: '#4a90d8', desc: 'People presence fraction' },
  { id: 'lighting_internal',  label: 'Internal Lighting',      unit: '%',      color: '#fbbf24', desc: 'Interior lighting power fraction (ECSBC §9.2)' },
  { id: 'lighting_external',  label: 'External Lighting',      unit: '%',      color: '#f59e0b', desc: 'Exterior/facade lighting — dusk-to-dawn (ECSBC §9.4)' },
  { id: 'equipment_internal', label: 'Internal Equipment',     unit: '%',      color: '#34d399', desc: 'Interior plug loads / process equipment (ECSBC §9.5)' },
  { id: 'equipment_external', label: 'External Equipment',     unit: '%',      color: '#10b981', desc: 'Exterior equipment — cooling towers, signage (ECSBC §9.6)' },
  { id: 'infiltration',       label: 'Infiltration',           unit: '%',      color: '#f87171', desc: 'Infiltration rate fraction' },
  { id: 'outdoor_air',        label: 'Outdoor Air',            unit: '%',      color: '#a78bfa', desc: 'Ventilation availability' },
  { id: 'hvac',               label: 'HVAC Availability',      unit: 'On/Off', color: '#06b6d4', desc: 'HVAC system on/off' },
  { id: 'cooling_sp',         label: 'Cooling Setpoint',       unit: '°C',     color: '#38bdf8', desc: 'Cooling setpoint schedule', isSetpoint: true },
  { id: 'heating_sp',         label: 'Heating Setpoint',       unit: '°C',     color: '#fb923c', desc: 'Heating setpoint schedule', isSetpoint: true },
];

// Backward-compat: old projects stored schedules under 'lighting' / 'equipment'.
// When looking up a new sub-type, also check the old key as a fallback.
//   new type id   →  old key to check in stored data
export const SCHEDULE_TYPE_ALIASES = {
  lighting_internal:  'lighting',
  equipment_internal: 'equipment',
};

/**
 * A catch-all for a custom schedule that is not one of the code's own types — a lift, a pump, a
 * process load. It is deliberately NOT in SCHEDULE_TYPES: it must never become a tab of its own
 * in the editor, and no zone inherits it. It exists only as a type a user can give to a schedule
 * they authored. Whether it carries fractions or degrees is chosen per schedule (`units`).
 */
export const OTHER_SCHEDULE_TYPE = {
  id: 'other', label: 'Other', unit: '%', color: '#94a3b8',
  desc: 'Anything the code types do not cover',
};

export const SCHEDULE_TYPE_MAP = Object.fromEntries(
  [...SCHEDULE_TYPES, OTHER_SCHEDULE_TYPE].map((t) => [t.id, t]),
);

// Interior types — zone / floor / building cascade (used in zone editor)
export const INTERIOR_SCHEDULE_TYPES = SCHEDULE_TYPES.filter(
  (t) => !['lighting_external', 'equipment_external'].includes(t.id),
);
// Exterior types — building level only (used in site editor)
export const EXTERIOR_SCHEDULE_TYPES = SCHEDULE_TYPES.filter(
  (t) => ['lighting_external', 'equipment_external'].includes(t.id),
);

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Repeat a pattern to fill a 24-slot array */
function h(arr) {
  if (arr.length === 24) return arr;
  const out = [];
  for (let i = 0; i < 24; i++) out.push(arr[i % arr.length] ?? 0);
  return out;
}

/** Uniform value for all 24 hours */
const fill = (v) => Array(24).fill(v);

/** Ramp from `from` to `to` over `hours` starting at index `start` */
function ramp(base, start, hours, from, to) {
  const r = [...base];
  for (let i = 0; i < hours; i++) r[start + i] = from + (to - from) * (i / (hours - 1));
  return r;
}

// ── ECSBC 2024 shared exterior profiles ──────────────────────────────────────
//
// Exterior Lighting (ECSBC 2024 §9.4):  Astronomical dusk-to-dawn control.
//   Full power 18:00–23:00 (evening peak), dimmed 30 % 23:00–06:00 (overnight),
//   off 06:00–18:00 (daylight).  Same profile applies any day of the week.
const EXT_LIGHTING_PROFILE = h([
  0.30,0.30,0.30,0.30,0.30,0.30,   // 00–06  dim overnight
  0.00,0.00,0.00,0.00,0.00,0.00,   // 06–12  off (daylight)
  0.00,0.00,0.00,0.00,0.00,0.00,   // 12–18  off (daylight)
  1.00,1.00,1.00,1.00,1.00,0.30,   // 18–24  full evening + late dim
]);

// Exterior Equipment (ECSBC 2024 §9.6):  Cooling towers / condensers follow
//   HVAC availability; exterior signage follows lighting.  Weighted 80 % HVAC
//   fraction + 20 % lighting fraction gives a realistic composite profile.
function makeExtEquip(hvacWD, hvacWE) {
  const blend = (h, l) => Math.round((0.8 * h + 0.2 * l) * 100) / 100;
  return {
    weekday: EXT_LIGHTING_PROFILE.map((l, i) => blend(hvacWD[i], l)),
    weekend: EXT_LIGHTING_PROFILE.map((l, i) => blend(hvacWE[i], l)),
  };
}

// ── Weekday/Weekend profiles per template ──────────────────────────────────────

const BASE_TEMPLATES = {

  // ── OFFICE (ECSBC 2024 §12.7.1) ───────────────────────────────────────────
  office: {
    label: 'Office',
    occupancy: {
      weekday: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.25,0.60,0.95,1.00,1.00,1.00,
                  0.85,1.00,1.00,1.00, 0.90,0.70,0.40,0.15,0.08,0.05,0.05,0.05]),
      weekend:  h([0.05,0.05,0.05,0.05,0.05,0.05, 0.05,0.10,0.15,0.20,0.20,0.15,
                   0.15,0.15,0.10,0.10, 0.05,0.05,0.05,0.05,0.05,0.05,0.05,0.05]),
    },
    // Internal lighting (same as legacy 'lighting') — interior LPD schedule
    lighting_internal: {
      weekday: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.30,0.70,0.90,0.95,0.95,0.95,
                  0.90,0.95,0.95,0.95, 0.90,0.75,0.50,0.20,0.10,0.05,0.05,0.05]),
      weekend:  h([0.05,0.05,0.05,0.05,0.05,0.05, 0.05,0.10,0.15,0.20,0.20,0.20,
                   0.15,0.15,0.10,0.10, 0.05,0.05,0.05,0.05,0.05,0.05,0.05,0.05]),
    },
    // External lighting — facade, parking, security (ECSBC §9.4 dusk-to-dawn)
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    // Internal equipment (same as legacy 'equipment') — plug loads schedule
    equipment_internal: {
      weekday: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.35,0.65,0.90,0.95,0.95,0.95,
                  0.85,0.95,0.95,0.95, 0.85,0.65,0.45,0.20,0.15,0.10,0.10,0.10]),
      weekend:  h([0.10,0.10,0.10,0.10,0.10,0.10, 0.10,0.15,0.20,0.25,0.25,0.20,
                   0.20,0.20,0.15,0.15, 0.10,0.10,0.10,0.10,0.10,0.10,0.10,0.10]),
    },
    // External equipment — cooling towers, condensers, signage (ECSBC §9.6)
    equipment_external: makeExtEquip(
      h([0,0,0,0,0,0, 1,1,1,1,1,1, 1,1,1,1, 1,1,1,0, 0,0,0,0]),
      h([0,0,0,0,0,0, 0,1,1,1,1,1, 1,1,0,0, 0,0,0,0, 0,0,0,0]),
    ),
    // Legacy aliases — kept for backward compatibility with existing projects
    lighting:  { weekday: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.30,0.70,0.90,0.95,0.95,0.95, 0.90,0.95,0.95,0.95, 0.90,0.75,0.50,0.20,0.10,0.05,0.05,0.05]), weekend: h([0.05,0.05,0.05,0.05,0.05,0.05, 0.05,0.10,0.15,0.20,0.20,0.20, 0.15,0.15,0.10,0.10, 0.05,0.05,0.05,0.05,0.05,0.05,0.05,0.05]) },
    equipment: { weekday: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.35,0.65,0.90,0.95,0.95,0.95, 0.85,0.95,0.95,0.95, 0.85,0.65,0.45,0.20,0.15,0.10,0.10,0.10]), weekend: h([0.10,0.10,0.10,0.10,0.10,0.10, 0.10,0.15,0.20,0.25,0.25,0.20, 0.20,0.20,0.15,0.15, 0.10,0.10,0.10,0.10,0.10,0.10,0.10,0.10]) },
    infiltration: {
      weekday: h([1.00,1.00,1.00,1.00,1.00,0.90, 0.70,0.50,0.25,0.20,0.20,0.20,
                  0.25,0.20,0.20,0.20, 0.25,0.40,0.60,0.80,0.90,1.00,1.00,1.00]),
      weekend:  fill(1.00),
    },
    outdoor_air: {
      weekday: h([0.00,0.00,0.00,0.00,0.00,0.00, 0.20,0.80,1.00,1.00,1.00,1.00,
                  1.00,1.00,1.00,1.00, 1.00,0.80,0.40,0.00,0.00,0.00,0.00,0.00]),
      weekend:  h([0.00,0.00,0.00,0.00,0.00,0.00, 0.00,0.20,0.40,0.40,0.40,0.40,
                   0.40,0.40,0.20,0.00, 0.00,0.00,0.00,0.00,0.00,0.00,0.00,0.00]),
    },
    hvac: {
      weekday: h([0,0,0,0,0,0, 1,1,1,1,1,1, 1,1,1,1, 1,1,1,0, 0,0,0,0]),
      weekend:  h([0,0,0,0,0,0, 0,1,1,1,1,1, 1,1,0,0, 0,0,0,0, 0,0,0,0]),
    },
    cooling_sp: {
      weekday: h([28,28,28,28,28,28, 26,25,24,24,24,24, 24,24,24,24, 24,25,26,27,28,28,28,28]),
      weekend:  fill(28),
    },
    heating_sp: {
      weekday: h([15,15,15,15,15,15, 19,21,21,21,21,21, 21,21,21,21, 21,21,20,18,16,15,15,15]),
      weekend:  fill(15),
    },
  },

  // ── RETAIL (ECSBC 2024 §12.7.2) ───────────────────────────────────────────
  retail: {
    label: 'Retail',
    occupancy: {
      weekday: h([0.02,0.02,0.02,0.02,0.02,0.05, 0.10,0.20,0.40,0.70,0.85,0.90,
                  0.85,0.85,0.90,0.95, 1.00,0.95,0.90,0.85,0.70,0.50,0.20,0.05]),
      weekend:  h([0.02,0.02,0.02,0.02,0.02,0.05, 0.10,0.30,0.60,0.90,1.00,1.00,
                   1.00,1.00,1.00,0.95, 0.95,0.90,0.85,0.70,0.50,0.20,0.05,0.02]),
    },
    lighting_internal: { weekday: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.20,0.50,0.80,0.95,1.00,1.00, 1.00,1.00,1.00,1.00, 1.00,1.00,0.95,0.90,0.80,0.60,0.30,0.10]), weekend: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.20,0.60,0.90,1.00,1.00,1.00, 1.00,1.00,1.00,1.00, 1.00,1.00,0.95,0.85,0.70,0.40,0.15,0.05]) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.20,0.40,0.70,0.90,0.95,0.95, 0.95,0.95,0.95,0.95, 0.95,0.90,0.80,0.70,0.50,0.30,0.15,0.10]), weekend: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.25,0.60,0.85,0.95,1.00,1.00, 1.00,1.00,1.00,0.95, 0.95,0.90,0.80,0.65,0.40,0.20,0.10,0.10]) },
    equipment_external: makeExtEquip(h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0]), h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0])),
    lighting:  { weekday: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.20,0.50,0.80,0.95,1.00,1.00, 1.00,1.00,1.00,1.00, 1.00,1.00,0.95,0.90,0.80,0.60,0.30,0.10]), weekend: h([0.05,0.05,0.05,0.05,0.05,0.10, 0.20,0.60,0.90,1.00,1.00,1.00, 1.00,1.00,1.00,1.00, 1.00,1.00,0.95,0.85,0.70,0.40,0.15,0.05]) },
    equipment: { weekday: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.20,0.40,0.70,0.90,0.95,0.95, 0.95,0.95,0.95,0.95, 0.95,0.90,0.80,0.70,0.50,0.30,0.15,0.10]), weekend: h([0.10,0.10,0.10,0.10,0.10,0.15, 0.25,0.60,0.85,0.95,1.00,1.00, 1.00,1.00,1.00,0.95, 0.95,0.90,0.80,0.65,0.40,0.20,0.10,0.10]) },
    infiltration: { weekday: h([1,1,1,1,1,1,0.8,0.6,0.4,0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.6,0.8,0.9,1,1]), weekend: fill(1) },
    outdoor_air:  { weekday: h([0,0,0,0,0,0,0,0.5,1,1,1,1,1,1,1,1,1,1,0.5,0,0,0,0,0]), weekend: h([0,0,0,0,0,0,0,0.5,1,1,1,1,1,1,1,1,1,1,0.5,0,0,0,0,0]) },
    hvac:         { weekday: h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0]), weekend: h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0]) },
    cooling_sp:   { weekday: h([28,28,28,28,28,28,27,25,24,24,24,24,24,24,24,24,24,24,24,25,26,27,28,28]), weekend: fill(28) },
    heating_sp:   { weekday: h([15,15,15,15,15,15,18,20,21,21,21,21,21,21,21,21,21,21,21,20,18,16,15,15]), weekend: fill(15) },
  },

  // ── HOSPITAL / HEALTHCARE (24-hour) ────────────────────────────────────────
  hospital: {
    label: 'Hospital / Healthcare',
    occupancy: {
      weekday: h([0.80,0.80,0.80,0.80,0.80,0.85, 0.90,1.00,1.00,1.00,1.00,1.00,
                  1.00,1.00,1.00,1.00, 1.00,1.00,0.95,0.90,0.85,0.85,0.80,0.80]),
      weekend:  h([0.80,0.80,0.80,0.80,0.80,0.85, 0.90,0.95,0.95,0.95,0.95,0.95,
                   0.95,0.95,0.95,0.95, 0.90,0.90,0.85,0.85,0.80,0.80,0.80,0.80]),
    },
    lighting_internal: { weekday: h([0.40,0.40,0.40,0.40,0.40,0.50,0.70,0.90,1.00,1.00,1.00,1.00,1.00,1.00,1.00,1.00,1.00,0.90,0.80,0.70,0.60,0.50,0.40,0.40]), weekend: h([0.40,0.40,0.40,0.40,0.40,0.50,0.60,0.80,0.90,0.90,0.90,0.90,0.90,0.90,0.90,0.90,0.80,0.70,0.60,0.50,0.45,0.40,0.40,0.40]) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: fill(0.70), weekend: fill(0.65) },
    equipment_external: makeExtEquip(fill(1), fill(1)), // 24h hospital: cooling systems always on
    lighting:     { weekday: h([0.40,0.40,0.40,0.40,0.40,0.50,0.70,0.90,1.00,1.00,1.00,1.00,1.00,1.00,1.00,1.00,1.00,0.90,0.80,0.70,0.60,0.50,0.40,0.40]), weekend: h([0.40,0.40,0.40,0.40,0.40,0.50,0.60,0.80,0.90,0.90,0.90,0.90,0.90,0.90,0.90,0.90,0.80,0.70,0.60,0.50,0.45,0.40,0.40,0.40]) },
    equipment:    { weekday: fill(0.70), weekend: fill(0.65) },
    infiltration: { weekday: fill(0.25), weekend: fill(0.25) },
    outdoor_air:  { weekday: fill(1.00), weekend: fill(1.00) },
    hvac:         { weekday: fill(1), weekend: fill(1) },
    cooling_sp:   { weekday: fill(24), weekend: fill(24) },
    heating_sp:   { weekday: fill(21), weekend: fill(21) },
  },

  // ── EDUCATIONAL / SCHOOL ───────────────────────────────────────────────────
  educational: {
    label: 'Educational / School',
    occupancy: {
      weekday: h([0.00,0.00,0.00,0.00,0.00,0.00, 0.05,0.20,0.95,1.00,1.00,1.00,
                  0.50,1.00,1.00,1.00, 0.80,0.30,0.10,0.05,0.00,0.00,0.00,0.00]),
      weekend:  h([0.00,0.00,0.00,0.00,0.00,0.00, 0.00,0.00,0.05,0.10,0.15,0.15,
                   0.10,0.10,0.05,0.05, 0.00,0.00,0.00,0.00,0.00,0.00,0.00,0.00]),
    },
    lighting_internal: { weekday: h([0,0,0,0,0,0,0.05,0.30,0.90,0.95,0.95,0.95,0.60,0.95,0.95,0.90,0.70,0.20,0.05,0,0,0,0,0]), weekend: fill(0.05) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: h([0,0,0,0,0,0,0.05,0.25,0.85,0.90,0.90,0.90,0.55,0.90,0.90,0.85,0.60,0.20,0.05,0,0,0,0,0]), weekend: fill(0.05) },
    equipment_external: makeExtEquip(h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0,0,0]), fill(0)),
    lighting:     { weekday: h([0,0,0,0,0,0,0.05,0.30,0.90,0.95,0.95,0.95,0.60,0.95,0.95,0.90,0.70,0.20,0.05,0,0,0,0,0]), weekend: fill(0.05) },
    equipment:    { weekday: h([0,0,0,0,0,0,0.05,0.25,0.85,0.90,0.90,0.90,0.55,0.90,0.90,0.85,0.60,0.20,0.05,0,0,0,0,0]), weekend: fill(0.05) },
    infiltration: { weekday: h([1,1,1,1,1,1,0.9,0.7,0.3,0.2,0.2,0.2,0.3,0.2,0.2,0.3,0.5,0.7,0.9,1,1,1,1,1]), weekend: fill(1) },
    outdoor_air:  { weekday: h([0,0,0,0,0,0,0,0.3,1,1,1,1,0.8,1,1,1,0.8,0.3,0,0,0,0,0,0]), weekend: fill(0) },
    hvac:         { weekday: h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0,0,0]), weekend: fill(0) },
    cooling_sp:   { weekday: h([28,28,28,28,28,28,27,26,24,24,24,24,25,24,24,24,25,27,28,28,28,28,28,28]), weekend: fill(28) },
    heating_sp:   { weekday: h([15,15,15,15,15,15,18,20,21,21,21,21,21,21,21,21,21,18,15,15,15,15,15,15]), weekend: fill(15) },
  },

  // ── HOTEL / HOSPITALITY ────────────────────────────────────────────────────
  hotel: {
    label: 'Hotel / Hospitality',
    occupancy: {
      weekday: h([0.80,0.85,0.90,0.90,0.80,0.60, 0.30,0.20,0.15,0.20,0.25,0.35,
                  0.40,0.35,0.30,0.35, 0.50,0.65,0.75,0.85,0.90,0.90,0.85,0.80]),
      weekend:  h([0.85,0.90,0.90,0.90,0.85,0.65, 0.40,0.30,0.25,0.30,0.35,0.45,
                   0.50,0.50,0.45,0.50, 0.60,0.70,0.80,0.90,0.95,0.95,0.90,0.85]),
    },
    lighting_internal: { weekday: h([0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.6,0.7,0.7,0.7,0.8,0.8,0.7,0.7,0.7,0.8,0.9,1.0,1.0,0.9,0.8,0.6,0.4]), weekend: h([0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.6,0.7,0.8,0.8,0.8,0.8,0.8,0.8,0.8,0.8,0.9,1.0,1.0,1.0,0.9,0.7,0.5]) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: h([0.4,0.4,0.4,0.4,0.4,0.5,0.6,0.7,0.7,0.6,0.6,0.7,0.7,0.6,0.6,0.6,0.7,0.8,0.9,0.9,0.9,0.8,0.6,0.5]), weekend: h([0.4,0.4,0.4,0.4,0.4,0.5,0.6,0.7,0.8,0.7,0.7,0.7,0.7,0.7,0.7,0.7,0.8,0.9,1.0,1.0,0.9,0.8,0.6,0.5]) },
    equipment_external: makeExtEquip(fill(1), fill(1)), // hotel: continuous cooling plant
    lighting:     { weekday: h([0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.6,0.7,0.7,0.7,0.8,0.8,0.7,0.7,0.7,0.8,0.9,1.0,1.0,0.9,0.8,0.6,0.4]), weekend: h([0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.6,0.7,0.8,0.8,0.8,0.8,0.8,0.8,0.8,0.8,0.9,1.0,1.0,1.0,0.9,0.7,0.5]) },
    equipment:    { weekday: h([0.4,0.4,0.4,0.4,0.4,0.5,0.6,0.7,0.7,0.6,0.6,0.7,0.7,0.6,0.6,0.6,0.7,0.8,0.9,0.9,0.9,0.8,0.6,0.5]), weekend: h([0.4,0.4,0.4,0.4,0.4,0.5,0.6,0.7,0.8,0.7,0.7,0.7,0.7,0.7,0.7,0.7,0.8,0.9,1.0,1.0,0.9,0.8,0.6,0.5]) },
    infiltration: { weekday: fill(0.50), weekend: fill(0.50) },
    outdoor_air:  { weekday: fill(1.00), weekend: fill(1.00) },
    hvac:         { weekday: fill(1), weekend: fill(1) },
    cooling_sp:   { weekday: fill(24), weekend: fill(24) },
    heating_sp:   { weekday: fill(21), weekend: fill(21) },
  },

  // ── RESIDENTIAL ────────────────────────────────────────────────────────────
  residential: {
    label: 'Residential',
    occupancy: {
      weekday: h([0.90,0.92,0.95,0.95,0.90,0.80, 0.60,0.35,0.20,0.15,0.15,0.20,
                  0.25,0.20,0.20,0.25, 0.30,0.45,0.65,0.80,0.88,0.90,0.90,0.90]),
      weekend:  h([0.90,0.90,0.90,0.90,0.90,0.85, 0.70,0.55,0.45,0.45,0.50,0.55,
                   0.55,0.55,0.55,0.60, 0.65,0.70,0.80,0.85,0.90,0.90,0.90,0.90]),
    },
    lighting_internal: { weekday: h([0.1,0.1,0.1,0.1,0.1,0.2,0.5,0.6,0.3,0.2,0.2,0.2,0.3,0.2,0.2,0.2,0.3,0.5,0.7,0.8,0.8,0.7,0.4,0.2]), weekend: h([0.1,0.1,0.1,0.1,0.1,0.1,0.2,0.5,0.6,0.5,0.4,0.4,0.4,0.4,0.4,0.4,0.5,0.7,0.8,0.8,0.8,0.7,0.4,0.2]) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: h([0.3,0.3,0.3,0.3,0.3,0.4,0.6,0.7,0.5,0.4,0.4,0.4,0.5,0.4,0.4,0.4,0.5,0.7,0.8,0.8,0.8,0.7,0.5,0.3]), weekend: h([0.3,0.3,0.3,0.3,0.3,0.3,0.4,0.6,0.7,0.7,0.6,0.6,0.6,0.6,0.6,0.6,0.7,0.8,0.8,0.8,0.8,0.7,0.5,0.3]) },
    equipment_external: makeExtEquip(h([1,1,1,1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,1,1,1,1,1,1]), fill(1)),
    lighting:     { weekday: h([0.1,0.1,0.1,0.1,0.1,0.2,0.5,0.6,0.3,0.2,0.2,0.2,0.3,0.2,0.2,0.2,0.3,0.5,0.7,0.8,0.8,0.7,0.4,0.2]), weekend: h([0.1,0.1,0.1,0.1,0.1,0.1,0.2,0.5,0.6,0.5,0.4,0.4,0.4,0.4,0.4,0.4,0.5,0.7,0.8,0.8,0.8,0.7,0.4,0.2]) },
    equipment:    { weekday: h([0.3,0.3,0.3,0.3,0.3,0.4,0.6,0.7,0.5,0.4,0.4,0.4,0.5,0.4,0.4,0.4,0.5,0.7,0.8,0.8,0.8,0.7,0.5,0.3]), weekend: h([0.3,0.3,0.3,0.3,0.3,0.3,0.4,0.6,0.7,0.7,0.6,0.6,0.6,0.6,0.6,0.6,0.7,0.8,0.8,0.8,0.8,0.7,0.5,0.3]) },
    infiltration: { weekday: fill(0.40), weekend: fill(0.40) },
    outdoor_air:  { weekday: h([0,0,0,0,0,0,0,0.5,0.5,0,0,0,0,0,0,0,0,0.5,0.5,0,0,0,0,0]), weekend: h([0,0,0,0,0,0,0,0.3,0.5,0.5,0.3,0,0,0,0,0,0.3,0.5,0.5,0.3,0,0,0,0]) },
    hvac:         { weekday: h([1,1,1,1,1,1,1,1,0,0,0,0,0,0,0,0,0,0,1,1,1,1,1,1]), weekend: fill(1) },
    cooling_sp:   { weekday: h([27,27,27,27,27,27,27,26,26,26,26,26,26,26,26,26,26,26,25,25,25,26,26,27]), weekend: fill(25) },
    heating_sp:   { weekday: h([19,19,19,19,19,19,20,21,18,18,18,18,18,18,18,18,18,18,20,21,21,21,20,19]), weekend: fill(20) },
  },

  // ── ASSEMBLY / MIXED USE ─────────────────────────────────────────────────────
  // The 'assembly' building type resolves here (see BUILDING_TYPE_TEMPLATE); the ECSBC §12.7
  // overlay below replaces its operating schedules with the Assembly (Seating/Public) profile.
  mixed: {
    label: 'Assembly / Mixed Use',
    occupancy: {
      weekday: h([0.20,0.20,0.20,0.20,0.20,0.25, 0.30,0.45,0.70,0.80,0.85,0.85,
                  0.80,0.85,0.85,0.85, 0.80,0.75,0.70,0.65,0.60,0.50,0.35,0.25]),
      weekend:  h([0.30,0.30,0.30,0.30,0.30,0.35, 0.40,0.55,0.70,0.80,0.85,0.85,
                   0.80,0.85,0.85,0.80, 0.75,0.70,0.65,0.60,0.55,0.45,0.35,0.30]),
    },
    lighting_internal: { weekday: h([0.1,0.1,0.1,0.1,0.1,0.2,0.3,0.5,0.8,0.9,0.9,0.9,0.85,0.9,0.9,0.9,0.85,0.8,0.7,0.6,0.5,0.4,0.2,0.1]), weekend: h([0.1,0.1,0.1,0.1,0.1,0.2,0.3,0.5,0.8,0.9,0.9,0.9,0.9,0.9,0.9,0.9,0.85,0.8,0.7,0.6,0.5,0.3,0.2,0.1]) },
    lighting_external: { weekday: EXT_LIGHTING_PROFILE, weekend: EXT_LIGHTING_PROFILE },
    equipment_internal: { weekday: h([0.2,0.2,0.2,0.2,0.2,0.25,0.35,0.55,0.75,0.85,0.85,0.85,0.80,0.85,0.85,0.85,0.8,0.7,0.6,0.5,0.4,0.3,0.2,0.2]), weekend: h([0.2,0.2,0.2,0.2,0.2,0.25,0.3,0.5,0.7,0.8,0.85,0.85,0.85,0.85,0.85,0.8,0.75,0.7,0.6,0.5,0.4,0.3,0.2,0.2]) },
    equipment_external: makeExtEquip(h([0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0]), h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0])),
    lighting:     { weekday: h([0.1,0.1,0.1,0.1,0.1,0.2,0.3,0.5,0.8,0.9,0.9,0.9,0.85,0.9,0.9,0.9,0.85,0.8,0.7,0.6,0.5,0.4,0.2,0.1]), weekend: h([0.1,0.1,0.1,0.1,0.1,0.2,0.3,0.5,0.8,0.9,0.9,0.9,0.9,0.9,0.9,0.9,0.85,0.8,0.7,0.6,0.5,0.3,0.2,0.1]) },
    equipment:    { weekday: h([0.2,0.2,0.2,0.2,0.2,0.25,0.35,0.55,0.75,0.85,0.85,0.85,0.80,0.85,0.85,0.85,0.8,0.7,0.6,0.5,0.4,0.3,0.2,0.2]), weekend: h([0.2,0.2,0.2,0.2,0.2,0.25,0.3,0.5,0.7,0.8,0.85,0.85,0.85,0.85,0.85,0.8,0.75,0.7,0.6,0.5,0.4,0.3,0.2,0.2]) },
    infiltration: { weekday: h([0.8,0.8,0.8,0.8,0.8,0.8,0.7,0.5,0.3,0.25,0.25,0.25,0.3,0.25,0.25,0.25,0.3,0.4,0.5,0.6,0.7,0.75,0.8,0.8]), weekend: fill(0.7) },
    outdoor_air:  { weekday: h([0,0,0,0,0,0,0.2,0.5,1,1,1,1,1,1,1,1,1,0.8,0.5,0.2,0,0,0,0]), weekend: h([0,0,0,0,0,0,0,0.3,0.7,1,1,1,1,1,1,0.8,0.6,0.4,0.2,0,0,0,0,0]) },
    hvac:         { weekday: h([0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0]), weekend: h([0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,0,0]) },
    cooling_sp:   { weekday: h([28,28,28,28,28,27,26,25,24,24,24,24,24,24,24,24,24,24,25,26,27,27,28,28]), weekend: fill(27) },
    heating_sp:   { weekday: h([16,16,16,16,16,18,20,21,21,21,21,21,21,21,21,21,21,21,20,19,17,16,16,16]), weekend: fill(18) },
  },

  // ── LIGHT MANUFACTURING ──────────────────────────────────────────────────────
  // Neither ECSBC 2024 §12.7 nor ECBC 2017 §9.6 tabulates a manufacturing operating schedule, so
  // under those two codes this type has no profile to overlay and falls back to this entry -
  // exactly as Residential does. Rather than invent 24-hour numbers, the base profile IS the
  // ASHRAE reference-model Light Manufacturing profile, which is real measured-model data.
  // scheduleSource() still reports "not covered by this code's schedule tables - the app's
  // engineering defaults apply" while ECSBC or ECBC is selected, which is the honest description
  // of what these are in that context.
  light_manufacturing: {
    label: 'Light Manufacturing',
    // Comes from the project reference IDF, not the DOE library, which has no manufacturing
    // prototype at all.
    ...ASHRAE_SCHEDULE_PROFILES.light_manufacturing,
    // Legacy aliases, carried by every other template.
    lighting:  ASHRAE_SCHEDULE_PROFILES.light_manufacturing.lighting_internal,
    equipment: ASHRAE_SCHEDULE_PROFILES.light_manufacturing.equipment_internal,
  },
};

// ── Overlay the verbatim code operating schedules, once per code version ──────
// Replaces occupancy / interior + exterior lighting / equipment / HVAC-availability with the
// official values for every building type the standard defines. Neither code's schedule tables
// cover infiltration, outdoor air or thermostat setpoints (those come from other sections), nor
// Residential/true Mixed-use — those keep the engineering defaults in BASE_TEMPLATES.
//   template key → profile key.  'mixed' is the template the 'assembly' building type resolves
//   to (see BUILDING_TYPE_TEMPLATE), so Assembly schedules are applied there.
//   'residential' is listed for ASHRAE, which does tabulate it; ECSBC and ECBC define no
//   residential profile, so overlay() simply skips the key for them.
const SCHEDULE_OVERLAY_KEYS = {
  office: 'office', retail: 'retail', hospital: 'hospital',
  educational: 'educational', hotel: 'hotel', mixed: 'assembly',
  residential: 'residential', light_manufacturing: 'light_manufacturing',
};
const PROFILES_BY_CODE = {
  ecsbc_2024: ECSBC_2024_SCHEDULE_PROFILES,
  ecbc_2017:  ECBC_2017_SCHEDULE_PROFILES,
  ashrae_901: ASHRAE_SCHEDULE_PROFILES,
};

// Every field a code profile is allowed to replace. A profile overlays only the fields it
// actually defines: ECSBC/ECBC tabulate the first five (their codes cover nothing else), while
// the ASHRAE reference model also carries infiltration, outdoor air and thermostat setpoints.
const OVERLAYABLE_FIELDS = [
  'occupancy', 'lighting_internal', 'equipment_internal', 'lighting_external',
  'equipment_external', 'infiltration', 'outdoor_air', 'hvac', 'cooling_sp', 'heating_sp',
];

/** Deep-enough clone: each template is a flat map of { weekday[], weekend[] } objects. */
function cloneTemplates(src) {
  const out = {};
  for (const [k, tpl] of Object.entries(src)) {
    const t = {};
    for (const [tk, tv] of Object.entries(tpl)) {
      t[tk] = (tv && Array.isArray(tv.weekday))
        ? { ...tv, weekday: [...tv.weekday], weekend: [...(tv.weekend || tv.weekday)] }
        : tv;
    }
    out[k] = t;
  }
  return out;
}

function overlay(profiles) {
  const set = cloneTemplates(BASE_TEMPLATES);
  for (const [tplKey, profKey] of Object.entries(SCHEDULE_OVERLAY_KEYS)) {
    const prof = profiles[profKey];
    if (!prof || !set[tplKey]) continue;
    for (const f of OVERLAYABLE_FIELDS) {
      if (prof[f]) set[tplKey][f] = prof[f];
    }
    // Legacy aliases mirror the interior schedules.
    if (prof.lighting_internal)  set[tplKey].lighting  = prof.lighting_internal;
    if (prof.equipment_internal) set[tplKey].equipment = prof.equipment_internal;
  }
  return set;
}

/** One fully-overlaid template set per code version, built once at module load. */
export const TEMPLATES_BY_CODE = Object.fromEntries(
  SCHEDULE_CODES.map((c) => [c.id, overlay(PROFILES_BY_CODE[c.id] || {})]),
);

/** The template set for a code version (falls back to the default code, never undefined). */
export function templatesFor(code) {
  return TEMPLATES_BY_CODE[resolveScheduleCode(code)];
}

/**
 * Default-code template set. Kept as a named export because several call sites only read
 * `TEMPLATES[key].label`, which is code-independent. Anything that reads PROFILE VALUES must
 * go through templatesFor(code) instead.
 */
export const TEMPLATES = TEMPLATES_BY_CODE[DEFAULT_SCHEDULE_CODE];

/**
 * Provenance for the code the project is actually on, for the Schedules header.
 *
 * The two Indian codes carry identical numbers, so while one of them is active the header names
 * both rather than implying the pick changes values. ASHRAE 90.1 genuinely differs, so when it is
 * active only it is named - claiming ECSBC alongside it would be false.
 *
 * @returns {{ label:string, tables:string|null, space:string|null, note:string|null }}
 */
export function scheduleSourcesAll(buildingType, code) {
  const active = resolveScheduleCodeFor(buildingType, code);
  const shown = CROSS_CHECKED_CODE_IDS.includes(active) && codeProfilesIdentical(buildingType)
    ? CROSS_CHECKED_CODE_IDS
    : [active];
  const sources = shown.map((id) => scheduleSource(buildingType, id));
  const covered = sources.filter((s) => s.table);
  if (!covered.length) {
    return { label: shown.map((id) => `${SCHEDULE_CODE_MAP[id].label} ${SCHEDULE_CODE_MAP[id].section}`).join(' / '),
      tables: null, space: null, note: sources[0].note, shared: shown.length > 1 };
  }
  return {
    label: covered.map((s) => `${s.label} ${s.section}`).join(' / '),
    tables: covered.map((s) => s.table).join('  ·  '),
    space: covered.find((s) => s.space)?.space || null,
    note: null,
    // True when the label names more than one code, i.e. the active code shares its numbers with
    // another. The UI uses this to decide whether "Code default" is an honest shorthand.
    shared: covered.length > 1,
  };
}

/**
 * Do the two code versions resolve to the SAME numbers for this building type?
 *
 * They currently do for every type: BEE carried the ECBC 2017 §9.6 tables into ECSBC 2024 §12.7
 * unchanged. Computed rather than hard-coded, so if either dataset is ever re-transcribed and
 * diverges, the UI stops claiming they match without anyone having to remember to update it.
 *
 * Scoped to the two Indian codes on purpose. ASHRAE 90.1 comes from a different source entirely
 * and is EXPECTED to differ, so including it would only ever return false and would say nothing
 * about the BEE carry-forward this function exists to watch.
 */
export function codeProfilesIdentical(buildingType) {
  const key = BUILDING_TYPE_TEMPLATE[buildingType] || 'office';
  const sets = CROSS_CHECKED_CODE_IDS.map((id) => TEMPLATES_BY_CODE[id]?.[key]).filter(Boolean);
  if (sets.length < 2) return false;
  const [a, ...rest] = sets;
  for (const other of rest) {
    for (const t of SCHEDULE_TYPES) {
      const x = a[t.id]; const y = other[t.id];
      if (!x || !y) { if (x || y) return false; continue; }
      for (const day of ['weekday', 'weekend']) {
        const dx = x[day] || []; const dy = y[day] || [];
        if (dx.length !== dy.length) return false;
        for (let i = 0; i < dx.length; i++) if (Math.abs(Number(dx[i]) - Number(dy[i])) > 1e-9) return false;
      }
    }
  }
  return true;
}

/** Where a building type's profile comes from, for citation in the UI and exports. */
export function scheduleSource(buildingType, code) {
  const c = resolveScheduleCodeFor(buildingType, code);
  const profKey = SCHEDULE_OVERLAY_KEYS[BUILDING_TYPE_TEMPLATE[buildingType] || 'office'];
  const meta = SCHEDULE_CODE_MAP[c];
  if (!profKey || !PROFILES_BY_CODE[c]?.[profKey]) {
    return { code: c, label: meta.label, table: null, space: null,
      note: 'Not covered by this code’s schedule tables — the app’s engineering defaults apply.' };
  }
  const src = c === 'ecbc_2017' ? ECBC_2017_SCHEDULE_SOURCES[profKey]
    : c === 'ashrae_901' ? ASHRAE_SCHEDULE_SOURCES[profKey]
      : null;
  return { code: c, label: meta.label, section: meta.section,
    table: src?.table || meta.ref, space: src?.space || null, note: null };
}

// Building type → template mapping (matches projectStore building_type values)
export const BUILDING_TYPE_TEMPLATE = {
  office:      'office',
  retail:      'retail',
  hospital:    'hospital',
  school:      'educational',
  residential: 'residential',
  hospitality: 'hotel',
  assembly:    'mixed',
  light_manufacturing: 'light_manufacturing',
  other:       'office',
};

// ── Code availability per building type ───────────────────────────────────────
//
// Only codes flagged `requiresProfile` are gated. The gate is COMPUTED from the datasets, not
// hard-coded, so adding an ASHRAE profile for another space type makes that building type
// selectable with no further edits here.

/** Codes cross-checked against each other by codeProfilesIdentical (the BEE carry-forward). */
const CROSS_CHECKED_CODE_IDS = ['ecsbc_2024', 'ecbc_2017'];

/** The profile key a building type resolves to, or undefined when it maps to no profile. */
function profileKeyFor(buildingType) {
  return SCHEDULE_OVERLAY_KEYS[BUILDING_TYPE_TEMPLATE[buildingType] || 'office'];
}

/** True when `code` may be used for `buildingType`. */
export function scheduleCodeCoversType(code, buildingType) {
  const meta = SCHEDULE_CODE_MAP[resolveScheduleCode(code)];
  if (!meta.requiresProfile) return true;
  const key = profileKeyFor(buildingType);
  return !!(key && PROFILES_BY_CODE[meta.id]?.[key]);
}

/** The codes offerable for a building type, in declaration order. */
export function availableScheduleCodes(buildingType) {
  return SCHEDULE_CODES.filter((c) => scheduleCodeCoversType(c.id, buildingType));
}

/**
 * Normalise a stored code to one that is valid FOR THIS BUILDING TYPE.
 *
 * Every default-resolution path goes through this, so a project that picked ASHRAE as an office
 * and was later switched to a hospital — or a hand-edited .big file — can never resolve ASHRAE
 * profiles for a type ASHRAE does not cover. It falls back to the default code instead.
 */
export function resolveScheduleCodeFor(buildingType, code) {
  const c = resolveScheduleCode(code);
  return scheduleCodeCoversType(c, buildingType) ? c : DEFAULT_SCHEDULE_CODE;
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Return the default schedules for a given building type.
 * Each type has weekday + weekend 24-hour profiles.
 */
export function getDefaultSchedules(buildingType, code) {
  // Building-type-aware on purpose: this is the single choke point every default resolution
  // passes through, so gating here is what makes an uncovered type impossible to model wrongly.
  const set = templatesFor(resolveScheduleCodeFor(buildingType, code));
  const key = BUILDING_TYPE_TEMPLATE[buildingType] || 'office';
  return set[key] || set.office;
}

/**
 * Build a blank schedule object (all zeros or sensible setpoint defaults).
 */
export function blankSchedule(typeId) {
  const t = SCHEDULE_TYPE_MAP[typeId];
  if (!t) return { weekday: fill(0), weekend: fill(0), template: 'custom' };
  if (t.isSetpoint) {
    const def = typeId === 'cooling_sp' ? 26 : 20;
    return { weekday: fill(def), weekend: fill(def), template: 'custom' };
  }
  return { weekday: fill(0), weekend: fill(0), template: 'custom' };
}

/**
 * Resolve the effective schedule for a zone/floor/building cascade.
 * Returns { weekday, weekend, template, source: 'zone'|'floor'|'building'|'default' }
 *
 * Backward-compat: old projects may store 'lighting' / 'equipment' keys.
 * Those are transparently promoted to 'lighting_internal' / 'equipment_internal'.
 */
export function resolveSchedule(typeId, buildingSchedules, floorSchedules, zoneSchedules, buildingType, code) {
  const aliasId = SCHEDULE_TYPE_ALIASES[typeId]; // e.g. 'lighting' → 'lighting_internal'

  // Zone override (new key first, then old alias)
  if (zoneSchedules?.[typeId])  return { ...zoneSchedules[typeId],  source: 'zone' };
  if (aliasId && zoneSchedules?.[aliasId]) return { ...zoneSchedules[aliasId], source: 'zone' };
  // Floor override
  if (floorSchedules?.[typeId]) return { ...floorSchedules[typeId], source: 'floor' };
  if (aliasId && floorSchedules?.[aliasId]) return { ...floorSchedules[aliasId], source: 'floor' };
  // Building-level custom
  if (buildingSchedules?.[typeId]) return { ...buildingSchedules[typeId], source: 'building' };
  if (aliasId && buildingSchedules?.[aliasId]) return { ...buildingSchedules[aliasId], source: 'building' };
  // Code default from building type (which code version is set on the Schedules screen)
  const defaults = getDefaultSchedules(buildingType, code);
  const sched = defaults[typeId];
  if (sched) return { ...sched, template: BUILDING_TYPE_TEMPLATE[buildingType] || 'office', source: 'default' };
  return { ...blankSchedule(typeId), source: 'default' };
}

// ── Override bookkeeping ─────────────────────────────────────────────────────
// A stored schedule only counts as a real customisation when its 24-hour VALUES actually
// differ from what the cascade would resolve to without it. Re-applying the template that is
// already in effect, or dragging a bar back to where it started, must not leave the schedule
// flagged as "overridden" (yellow dot + "Reset to … default").

const HOUR_TOL = 1e-6;

/** True when two schedules carry the same 24-hour weekday + weekend profile. */
export function schedulesEqual(a, b) {
  if (!a || !b) return false;
  const day = (s, k) => {
    const v = s?.[k];
    return Array.isArray(v) ? v : null;
  };
  for (const k of ['weekday', 'weekend']) {
    const x = day(a, k), y = day(b, k);
    if (!x || !y) {
      // Treat a missing profile as equal only when the other is missing too.
      if (x || y) return false;
      continue;
    }
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i++) {
      const dx = Number(x[i]), dy = Number(y[i]);
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) { if (x[i] !== y[i]) return false; continue; }
      if (Math.abs(dx - dy) > HOUR_TOL) return false;
    }
  }
  return true;
}

/** Strip a type (and its legacy alias) from a level's store — used to see what it would inherit. */
function withoutType(store, typeId) {
  if (!store) return {};
  const out = { ...store };
  delete out[typeId];
  const alias = SCHEDULE_TYPE_ALIASES[typeId];
  if (alias) delete out[alias];
  return out;
}

/**
 * What `typeId` would resolve to at `level` if that level carried no override for it.
 * @param {'building'|'floor'|'zone'} level
 */
export function inheritedSchedule(typeId, { building = {}, floor = {}, zone = {} }, buildingType, level, code) {
  if (level === 'zone')  return resolveSchedule(typeId, building, floor, withoutType(zone, typeId), buildingType, code);
  if (level === 'floor') return resolveSchedule(typeId, building, withoutType(floor, typeId), {}, buildingType, code);
  return resolveSchedule(typeId, withoutType(building, typeId), {}, {}, buildingType, code);
}

/** The raw stored override for `typeId` at `level` (honouring the legacy alias), or null. */
export function storedSchedule(typeId, stores, level) {
  const store = level === 'zone' ? stores.zone : level === 'floor' ? stores.floor : stores.building;
  if (!store) return null;
  const alias = SCHEDULE_TYPE_ALIASES[typeId];
  return store[typeId] || (alias ? store[alias] : null) || null;
}

/**
 * True when `typeId` is *effectively* overridden at `level` — i.e. something is stored AND it
 * differs from the inherited/code-default profile. Drives the tab dot and the Reset button, so
 * a no-op template re-apply (or an edit-and-undo) never looks like a customisation.
 */
export function isEffectiveOverride(typeId, stores, buildingType, level, code) {
  const stored = storedSchedule(typeId, stores, level);
  if (!stored) return false;
  return !schedulesEqual(stored, inheritedSchedule(typeId, stores, buildingType, level, code));
}

/**
 * Summarise a 24-hour profile: peak, trough, active hours.
 */
export function profileSummary(values) {
  if (!values || values.length < 24) return { peak: 0, trough: 0, activeHours: 0 };
  const max = Math.max(...values);
  const min = Math.min(...values);
  const threshold = max * 0.5;
  const activeHours = values.filter((v) => v >= threshold).length;
  return { peak: max, trough: min, activeHours };
}
